import { ExampleItem, CategoryInfo, ExampleComplexity } from '../types/example.types';

/**
 * Простой класс для регистрации и управления примерами PlaybookComponent
 * Минимальная версия без сложной логики для быстрой валидации
 */
export class ExampleRegistry {
  private examples = new Map<string, ExampleItem>();
  private categories = new Map<string, CategoryInfo>();

  /**
   * Регистрирует новый пример
   */
  registerExample(example: ExampleItem): void {
    if (!example.id) {
      console.warn('[ExampleRegistry] Example ID is required');
      return;
    }

    if (this.examples.has(example.id)) {
      console.warn(`[ExampleRegistry] Example with ID "${example.id}" already exists. Overriding.`);
    }

    this.examples.set(example.id, example);
  }

  /**
   * Получает пример по ID
   */
  getExample(id: string): ExampleItem | undefined {
    return this.examples.get(id);
  }

  /**
   * Получает все примеры для категории
   */
  getExamplesByCategory(categoryId: string): ExampleItem[] {
    const examples: ExampleItem[] = [];

    for (const example of this.examples.values()) {
      if (example.category === categoryId) {
        examples.push(example);
      }
    }

    // Сортируем по сложности: basic -> intermediate -> advanced
    return examples.sort((a, b) => {
      const complexityOrder = { basic: 1, intermediate: 2, advanced: 3 };
      return complexityOrder[a.complexity] - complexityOrder[b.complexity];
    });
  }

  /**
   * Получает все зарегистрированные примеры
   */
  getAllExamples(): ExampleItem[] {
    return Array.from(this.examples.values());
  }

  /**
   * Регистрирует категорию
   */
  registerCategory(category: CategoryInfo): void {
    if (!category.id) {
      console.warn('[ExampleRegistry] Category ID is required');
      return;
    }

    if (this.categories.has(category.id)) {
      console.warn(`[ExampleRegistry] Category with ID "${category.id}" already exists. Overriding.`);
    }

    this.categories.set(category.id, category);
  }

  /**
   * Получает все категории, отсортированные по order
   */
  getAllCategories(): CategoryInfo[] {
    const categories = Array.from(this.categories.values());
    return categories.sort((a, b) => a.order - b.order);
  }

  /**
   * Получает категорию по ID
   */
  getCategory(id: string): CategoryInfo | undefined {
    return this.categories.get(id);
  }

  /**
   * Получает количество примеров в категории
   */
  getCategoryExampleCount(categoryId: string): number {
    return this.getExamplesByCategory(categoryId).length;
  }

  /**
   * Поиск примеров по тегам
   */
  searchByTags(tags: string[]): ExampleItem[] {
    if (!tags.length) return [];

    const results: ExampleItem[] = [];
    const searchTags = tags.map(tag => tag.toLowerCase());

    for (const example of this.examples.values()) {
      const exampleTags = example.tags.map(tag => tag.toLowerCase());
      const hasMatchingTags = searchTags.some(tag =>
        exampleTags.some(exampleTag => exampleTag.includes(tag))
      );

      if (hasMatchingTags) {
        results.push(example);
      }
    }

    return results;
  }

  /**
   * Фильтр примеров по сложности
   */
  getExamplesByComplexity(complexity: ExampleComplexity): ExampleItem[] {
    const results: ExampleItem[] = [];

    for (const example of this.examples.values()) {
      if (example.complexity === complexity) {
        results.push(example);
      }
    }

    return results;
  }

  /**
   * Проверяет, существует ли пример с данным ID
   */
  hasExample(id: string): boolean {
    return this.examples.has(id);
  }

  /**
   * Проверяет, существует ли категория с данным ID
   */
  hasCategory(id: string): boolean {
    return this.categories.has(id);
  }

  /**
   * Удаляет пример по ID
   */
  removeExample(id: string): boolean {
    return this.examples.delete(id);
  }

  /**
   * Удаляет категорию по ID
   */
  removeCategory(id: string): boolean {
    return this.categories.delete(id);
  }

  /**
   * Получает статистику registry
   */
  getStats(): {
    totalExamples: number;
    totalCategories: number;
    examplesByComplexity: Record<ExampleComplexity, number>;
    categoriesWithCounts: Array<{ category: CategoryInfo; count: number }>;
  } {
    const examplesByComplexity: Record<ExampleComplexity, number> = {
      basic: 0,
      intermediate: 0,
      advanced: 0
    };

    for (const example of this.examples.values()) {
      examplesByComplexity[example.complexity]++;
    }

    const categoriesWithCounts = this.getAllCategories().map(category => ({
      category,
      count: this.getCategoryExampleCount(category.id)
    }));

    return {
      totalExamples: this.examples.size,
      totalCategories: this.categories.size,
      examplesByComplexity,
      categoriesWithCounts
    };
  }

  /**
   * Очищает весь registry
   */
  clear(): void {
    this.examples.clear();
    this.categories.clear();
  }
}